import React from 'react';
import Select, { underlinerFunc } from 'ui/select';

import Data from 'data';
import Locale from 'data/locale';
import Store from 'store';
import History from 'store/history';
import EditingContext from 'store/editingContext';

import './HistoryChart.scss';
import { formatNumber } from 'utils/format';
import Tooltip from 'ui/tooltip';
import buildSpline from './buildSpline';
import scrollIntoView from 'scroll-into-view-if-needed';

const _Ls = Locale("ui-skills.js", "skilldata");
const _L = Locale("ui-timeline.js");

const PointOffset = 50;
const PointWidth = 16;
const PointRadius = 7;
const LineWidth = 2;
const EaseDelay = 250;
const CanvasHeight = 124;

const StatSelect = {
  optionName(stat) {
    const parts = stat.split('$');
    if (parts.length === 2) {
      return _Ls(parts[1]);
    } else {
      return Data.localeTable.statList[stat]?.name;
    }
  },
  renderOption: (opt, search) => <span>{search ? underlinerFunc(StatSelect.optionName(opt), search) : StatSelect.optionName(opt)}</span>,
  renderValue: opt => <span>{StatSelect.optionName(opt)}</span>,
  searchFunc: (opt, search) => StatSelect.optionName(opt).match(search),
};

function replaceProfile(profile, state) {
  state.buildinfo = profile.buildinfo;
  return state;
}

function HistoryStat() {
  const [stat, setStat] = EditingContext.useState("trackStat", "damage");

  const profileId = Store.useProfile(profile => profile.id);
  const historyStore = History.useStore();

  const getOptions = React.useCallback(() => {
    const skillValues = historyStore.getState()[profileId]?.skillValues;
    const options = skillValues && Object.entries(skillValues).map(([name, {data}]) => ({
      title: name,
      options: Object.keys(data).map(stat => `${name}$${stat}`),
    }));
    return [...Object.keys(Data.localeTable.statList), ...options];
  }, [profileId, historyStore]);

  return (
    <Select className="d3p-history-stat"
            options={getOptions}
            renderOption={StatSelect.renderOption}
            renderValue={StatSelect.renderValue}
            value={stat}
            onChange={setStat}
            searchMiddle={true}
            searchLimit={10}
            searchFunc={StatSelect.searchFunc}
    />
  );
}

function getValue(entry, statParts) {
  if (statParts.length === 2) {
    return entry.skillValues?.[statParts[0]]?.data?.[statParts[1]];
  } else {
    return entry.values?.[statParts[0]];
  }
}

function changeName(change) {
  if (Data.itemSlots[change]) {
    return _L`Change ${Data.itemSlots[change].name}`;
  } else switch (change) {
  case "skills": return _L`Change Skills`;
  case "passives": return _L`Change Passives`;
  case "kanai": return _L`Change Cube`;
  case "paragon": return _L`Change Paragon`;
  case "follower": return _L`Change Follower`;
  case "class": return _L`Change Class`;
  case "seasonal": return _L`Change Seasonal`;
  case "import": return _L`Load Profile`;
  default: return null;
  }
}

function ValueTooltip({ value, curValue, depth, change, onFocus }) {
  const stat = EditingContext.useStateView("trackStat") || "damage";
  const parts = stat.split('$');

  React.useEffect(() => {
    onFocus(depth);
    return () => onFocus(null);
  }, [onFocus, depth]);

  const name = changeName(change);
  const statName = parts.length === 2 ? `${parts[0]} \u2013 ${_Ls(parts[1])}` : (Data.localeTable.statList[stat]?.name || _L`Unknown`);

  const diff = (curValue && curValue > 1 ? 100 * ((value - curValue) / curValue) : null);

  return (
    <div>
      {!!name && <p><span className="d3-color-gold">{name}</span></p>}
      <p>
        {statName}: <span className="d3-color-green">
          {formatNumber(value, 0, 10000)}
          {!!diff && ` (${(diff >= 0 ? "+" : "") + formatNumber(diff, 2)}%)`}
        </span>
      </p>
      {depth > 0 && <p><span className="d3-color-gray">{_L`Click to undo`}</span></p>}
      {depth < 0 && <p><span className="d3-color-gray">{_L`Click to redo`}</span></p>}
    </div>
  );
}

function HistoryPlot() {
  const stat = EditingContext.useStateView("trackStat") || "damage";
  const profileId = Store.useProfile(profile => profile.id);

  const dispatch = Store.useDispatch();
  const history = History.useSelector(state => state[profileId]);

  const entries = React.useMemo(() => {
    const entries = [];
    const statParts = stat.split('$');
    let depth = 0;
    for (let cur = history; cur; cur = cur.prev, depth += 1) {
      const value = getValue(cur, statParts);
      if (value != null) {
        entries.push({value, depth, change: cur.change, profile: cur.profile});
      }
    }
    entries.reverse();
    depth = -1;
    for (let cur = history.next; cur; cur = cur.next, depth -= 1) {
      const value = getValue(cur, statParts);
      if (value != null) {
        entries.push({value, depth, change: cur.change, profile: cur.profile});
      }
    }
    return entries;
  }, [stat, history]);

  const [minValueFinal, setMinValue] = React.useState(null);
  const [maxValueFinal, setMaxValue] = React.useState(null);
  const [highlight, setHighlight] = React.useState(null);

  const minValueRef = React.useRef();
  const maxValueRef = React.useRef();

  const containerRef = React.useRef();
  const canvasRef = React.useRef();
  const lastRef = React.useRef();

  const render = React.useCallback(() => {
    if (!canvasRef.current) return;
    const minValue = minValueRef.current, maxValue = maxValueRef.current;
    if (minValue == null || maxValue == null || isNaN(minValue) || isNaN(maxValue)) return;

    const ctx = canvasRef.current.getContext("2d");
    const {width, height} = canvasRef.current;
    ctx.clearRect(0, 0, width, height);
    const { color, backgroundColor } = window.getComputedStyle(canvasRef.current);
    const { color: disabledColor } = window.getComputedStyle(containerRef.current);

    const points = entries.map(({value, depth}, index) => ({
      pos: [
        index * PointOffset + PointWidth / 2,
        Math.round((value - maxValue) / (minValue - maxValue) * height),
      ],
      hover: depth === highlight,
      disabled: depth < 0,
    }));

    if (points.length > 1) {
      ctx.strokeStyle = color;
      ctx.lineWidth = LineWidth;
      ctx.beginPath();
      const [x, y, x2, y2] = buildSpline(points);
      ctx.moveTo(...points[0].pos);
      for (let i = 0; i < points.length - 1; ++i) {
        if (!points[i].disabled && points[i + 1].disabled) {
          ctx.stroke();
          ctx.strokeStyle = disabledColor;
          ctx.beginPath();
          ctx.moveTo(...points[i].pos);
        }
        ctx.bezierCurveTo(x[i], y[i], x2[i], y2[i], ...points[i + 1].pos);
      }
      ctx.stroke();
    }

    ctx.lineWidth = 1;
    for (const pt of points) {
      const curColor = (pt.disabled ? disabledColor : color);
      if (pt.hover) {
        ctx.fillStyle = backgroundColor;
        ctx.strokeStyle = curColor;
      } else {
        ctx.fillStyle = curColor;
        ctx.strokeStyle = backgroundColor;
      }
      ctx.beginPath();
      ctx.arc(...pt.pos, PointRadius / 2, 0, 2 * Math.PI, false);
      ctx.fill();
      ctx.stroke();
    }
  }, [entries, highlight]);

  React.useLayoutEffect(() => {
    const current = entries[entries.length - 1]?.value || 0;
    let minValue = entries.reduce((prev, val) => Math.min(prev, val.value), current);
    let maxValue = entries.reduce((prev, val) => Math.max(prev, val.value), current);
    if (maxValue - minValue < 1e-3) {
      minValue = current - 1;
      maxValue = current + 1;
    } else {
      const delta = (maxValue - minValue) * 0.2;
      minValue -= delta;
      maxValue += delta;
    }
    setMinValue(minValue);
    setMaxValue(maxValue);
    const prevMin = minValueRef.current, prevMax = maxValueRef.current;
    const prevDelta = Math.max(prevMax - prevMin, 1e-3);
    if (prevMin == null || prevMax == null || (maxValue - minValue) / prevDelta > 100 || prevDelta / (maxValue - minValue) > 100) {
      minValueRef.current = minValue;
      maxValueRef.current = maxValue;
      render();
    } else {
      let timeStart = null;
      let frame = window.requestAnimationFrame(function update(now) {
        if (timeStart == null) timeStart = now;
        const t = 1.0 - Math.min((now - timeStart) / EaseDelay, 1.0);
        minValueRef.current = minValue + (prevMin - minValue) * t;
        maxValueRef.current = maxValue + (prevMax - maxValue) * t;
        render();
        if (t > 0) {
          frame = window.requestAnimationFrame(update);
        } else {
          frame = null;
        }
      });
      return () => frame && window.cancelAnimationFrame(frame);
    }
  }, [entries, render]);

  React.useLayoutEffect(() => {
    if (lastRef.current) {
      scrollIntoView(lastRef.current, {scrollMode: "if-needed", block: "center", inline: "center", boundary: containerRef.current});
    }
  }, [entries]);

  const onUndo = React.useCallback(depth => {
    const entry = entries.find(e => e.depth === depth);
    if (entry) dispatch.setProfile(profile => replaceProfile(profile, entry.profile));
  }, [dispatch, entries]);

  React.useEffect(() => {
    function onKeyDown(e) {
      if (e.code === "KeyZ" && e.ctrlKey) {
        const dir = (e.shiftKey ? -1 : 1);
        let best = null;
        for (const entry of entries) {
          if (entry.depth * dir > 0 && (!best || (entry.depth - best.depth) * dir < 0)) {
            best = entry;
          }
        }
        if (best) dispatch.setProfile(profile => replaceProfile(profile, best.profile));
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [dispatch, entries]);

  if (!entries.length) return null;
  const current = entries.find(e => e.depth === 0);

  return (
    <div className="d3p-chart-container" ref={containerRef}>
      <div className="d3p-history-chart">
        <canvas width={(entries.length - 1) * PointOffset + PointWidth} height={CanvasHeight} ref={canvasRef}/>
        {minValueFinal != null && isFinite(minValueFinal) && maxValueFinal != null && isFinite(maxValueFinal) && entries.map(({value, depth, change}, index) => {
          const left = index * PointOffset + PointWidth / 2;
          const top = Math.round((value - maxValueFinal) / (minValueFinal - maxValueFinal) * CanvasHeight);
          return (
            <Tooltip key={depth} tooltip={<ValueTooltip value={value} curValue={current && current.value} depth={depth} change={change} onFocus={setHighlight}/>}>
              <span style={{left, top}} ref={depth === 0 ? lastRef : null} onClick={() => onUndo(depth)}/>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

export default React.memo(function HistoryChart() {
  return (
    <div className="d3p-HistoryChart">
      <HistoryStat/>
      <HistoryPlot/>
    </div>
  );
});
